// 登录态：/api/auth/me 校验 + 角色判断；退出清 token
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { api, ApiError, captureHashToken, getToken, setToken } from '../api/client';
import type { UserInfo } from '../api/types';
import i18n from '../i18n';

interface AuthState {
  user: UserInfo | null;
  booting: boolean;
  login: (username: string, password: string) => Promise<void>;
  mockLogin: (wecomUserid: string) => Promise<void>;
  logout: () => void;
  isSuper: boolean;
  canManage: boolean; // super 或 service_admin
}

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserInfo | null>(null);
  const [booting, setBooting] = useState(true);

  const loadMe = useCallback(async () => {
    if (!getToken()) {
      setUser(null);
      setBooting(false);
      return;
    }
    try {
      // 服务端 /me 的 data 即用户对象本体
      const me = await api.get<UserInfo>('/api/auth/me');
      setUser(me);
      // 登录用户语言偏好仅在用户没手动切换过时生效
      if (!localStorage.getItem('wesc_lang') && me.lang) {
        void i18n.changeLanguage(me.lang);
      }
    } catch (e) {
      // 仅真正的 401 才清除登录态；网络抖动（如服务重启）保留 token
      const real401 = e instanceof ApiError && e.status === 401;
      if (real401) setToken(null);
      setUser(null);
    } finally {
      setBooting(false);
    }
  }, []);

  useEffect(() => {
    captureHashToken();
    void loadMe();
  }, [loadMe]);

  const login = useCallback(async (username: string, password: string) => {
    const r = await api.post<{ token: string; user: UserInfo }>('/api/auth/login', { username, password });
    setToken(r.token);
    setUser(r.user);
  }, []);

  const mockLogin = useCallback(async (wecomUserid: string) => {
    const r = await api.post<{ token: string; user: UserInfo }>('/api/auth/mock-login', { wecomUserid });
    setToken(r.token);
    setUser(r.user);
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
  }, []);

  const isSuper = user?.role === 'super_admin';
  const canManage = isSuper || user?.role === 'service_admin';

  return <Ctx.Provider value={{ user, booting, login, mockLogin, logout, isSuper, canManage }}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error('AuthProvider missing');
  return v;
}
